import axios, { AxiosRequestConfig } from 'axios'
import dotize from 'dotize'

import storage from '../utils/localStorage'
import openApi, { OpenApiInterface } from './requests/openApi'
import localApi from './requests/localApi'

import { ERRORS } from '../constants/errors'
import { dispatch, getState } from '../redux/configureStore'
import { appActions } from '../redux'
import { ReduxState } from '../types'

const createApi = (requests: Function, apiUrl: string) => {
  const api = axios.create({
    baseURL: apiUrl,
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Channel: '8', // The magic channel field, which indicates that requests are coming from a 'digital' app
      // Return http status codes instead of 200 with status attribute OK/ERROR
      'X-Remove-RestResponse': true,
    },
    timeout: 120000,
    validateStatus: (status: number) =>
      // work with HTTP:304 as a successful response
      (status >= 200 && status < 300) || status === 304,
  })

  // X-Flow-Id can be changed during process so add it to the header dynamically
  api.interceptors.request.use((config: any) => {
    const reduxState: ReduxState = getState()
    // eslint-disable-next-line no-param-reassign
    config.headers['X-Flow-Id'] = reduxState.app.flowId
    return config
  })

  api.interceptors.response.use(
    response => ({ ...response, responseStatus: processResponseStatus(response) }),
    error => {
      if (error.message === 'Network Error') {
        // dispatch(appActions.showCentralErrorModal(ERRORS.NETWORK));
      } else if (error.response.status === 401) {
        dispatch(appActions.showCentralErrorModal(ERRORS.UNAUTHORIZED))
      } else if (error.response.status >= 500) {
        // dispatch(appActions.showCentralErrorModal(ERRORS.SERVER));
      } /* else {
        dispatch(
          appActions.showCentralErrorModal(API_ERRORS.TECHNICAL_DIFFICULTIES)
        );
      } */

      if (error.response) {
        return {
          ...error.response,
          message: error.message,
          stack: error.stack,
          responseStatus: processResponseStatus(error),
        }
      }

      return { ...error, responseStatus: processResponseStatus(error) }
    }
  )

  const securedConfig: AxiosRequestConfig = { headers: {} }
  if (storage.getItem('token')) {
    securedConfig.headers.Authorization = `Bearer ${storage.getItem('token')}`
  }

  return requests(api, securedConfig)
}

export enum Response {
  ok = 'OK',
  dataError = 'DATA_ERROR',
  networkError = 'NETWORK_ERROR',
  serverErrorRetry = 'SERVER_RETRY',
  serverErrorStop = 'SERVER_ERROR_STOP',
}

export interface ApiErrorInfo {
  type: Response
  errors?: any
  warnings?: any
}

export const processResponseStatus = (response: any): Response => {
  const status = response.status || response.request.status
  if (response.message === 'Network Error') {
    return Response.networkError
  } else if (status === 503) {
    return Response.serverErrorRetry
  } else if (status === 400) {
    return Response.dataError
  } else if ((status >= 200 && status < 300) || status === 304) {
    return Response.ok
  }

  return Response.serverErrorStop
}

export const parseErrors = (response: any, isWarning = false): any => {
  const data = isWarning ? response.data.fieldWarnings : response.data.fieldErrors

  if (Array.isArray(data)) {
    const errors: any = {}
    data.forEach((item: any) => {
      errors[item.field] = item.message
    })
    if (Object.keys(errors).length) {
      return dotize.backward(errors)
    }
  }

  return undefined
}

let instance: any

const initOpenApi = (apiUrl: string): void => {
  instance = createApi(openApi, apiUrl)
}

export const getOpenApiInstance = (): OpenApiInterface => {
  if (!instance) {
    throw new Error('Api doest exist')
  }
  return instance
}

export default {
  getOpenApiInstance,
  localApi,
  initOpenApi,
}
